import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { FireSafetyParameter } from '../../utils/syncMISToKPI';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface FireSafetyParametersChartProps {
  parameters: FireSafetyParameter[];
  /** Used for the download filename and chart title subtitle */
  reportName?: string;
  /** e.g. "2024-03-15" — shown in subtitle and filename */
  reportDate?: string;
  /** Premises name shown in subtitle */
  premisesName?: string;
  onCanvasReady?: (getImage: () => string) => void;
}

const truncate = (s: string, n = 32) =>
  s.length <= n ? s : s.slice(0, n - 1) + '…';

const barLabelPlugin = {
  id: 'fsBarLabels',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset: any, di: number) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden || !meta?.data?.length) return;
      meta.data.forEach((bar: any, idx: number) => {
        const val: number = dataset.data[idx];
        if (!val) return;
        const rightEdge = bar.x;
        const centerY = bar.y;
        const leftEdge = bar.base ?? 0;
        const barWidth = Math.abs(rightEdge - leftEdge);
        ctx.save();
        ctx.textBaseline = 'middle';
        if (di === 1) {
          if (barWidth > 28) {
            ctx.font = '10px Arial';
            ctx.fillStyle = 'rgba(100,100,100,0.7)';
            ctx.textAlign = 'right';
            ctx.fillText(String(val), rightEdge - 5, centerY);
          }
        } else {
          const pct: number | null = dataset._pcts?.[idx] ?? null;
          const label = pct != null
            ? `${val.toFixed(1)} (${pct.toFixed(0)}%)`
            : val.toFixed(1);
          ctx.font = 'bold 10px Arial';
          ctx.fillStyle = '#1f2937';
          ctx.textAlign = 'left';
          ctx.fillText(label, rightEdge + 5, centerY);
        }
        ctx.restore();
      });
    });
  },
};

const FireSafetyParametersChart: React.FC<FireSafetyParametersChartProps> = ({
  parameters,
  reportName,
  reportDate,
  premisesName,
  onCanvasReady,
}) => {
  const chartRef = useRef<any>(null);

  const fireSafetyParams = useMemo(
    () => parameters.filter(
      p =>
        p.parameter !== 'EXTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED' &&
        p.parameter !== 'INTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED',
    ),
    [parameters],
  );

  const totalScore = useMemo(() => parameters.reduce((s, p) => s + (p.score ?? 0), 0), [parameters]);
  const totalMaxScore = useMemo(() => parameters.reduce((s, p) => s + (p.maxScore ?? 0), 0), [parameters]);
  const aggPct = totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
  const chartHeight = Math.max(420, fireSafetyParams.length * 42);

  // ── Notify parent after chart renders ──────────────────────────────────────
  useEffect(() => {
    if (!onCanvasReady) return;
    const t = setTimeout(() => {
      onCanvasReady(() => {
        const chart = chartRef.current;
        if (!chart) return '';
        const src = chart.canvas as HTMLCanvasElement;
        const off = document.createElement('canvas');
        off.width = src.width; off.height = src.height;
        const ctx = off.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, off.width, off.height);
        ctx.drawImage(src, 0, 0);
        return off.toDataURL('image/png');
      });
    }, 300);
    return () => clearTimeout(t);
  }, [onCanvasReady, parameters]);

  // Build a meaningful filename
  const buildFileName = useCallback(() => {
    const parts = ['Fire_Safety_Parameters'];
    if (premisesName) parts.push(premisesName.replace(/[^a-zA-Z0-9]/g, '_'));
    if (reportDate) parts.push(reportDate.replace(/[^0-9\-]/g, ''));
    else parts.push(new Date().toISOString().slice(0, 10));
    return parts.join('_') + '.png';
  }, [premisesName, reportDate]);

  const handleDownload = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Draw white background before exporting (canvas is transparent by default)
    const srcCanvas: HTMLCanvasElement = chart.canvas;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = srcCanvas.width;
    exportCanvas.height = srcCanvas.height;
    const ctx = exportCanvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.drawImage(srcCanvas, 0, 0);

    const a = document.createElement('a');
    a.href = exportCanvas.toDataURL('image/png');
    a.download = buildFileName();
    a.click();
  }, [buildFileName]);

  const subtitleParts = [
    premisesName,
    reportDate ? new Date(reportDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null,
  ].filter(Boolean);

  const chartData = useMemo(() => ({
    labels: fireSafetyParams.map(p => truncate(p.parameter)),
    datasets: [
      {
        label: 'Score',
        data: fireSafetyParams.map(p => p.score ?? 0),
        _pcts: fireSafetyParams.map(p => p.percentage ?? null),
        backgroundColor: fireSafetyParams.map(p => {
          const pct = p.percentage ?? 0;
          return pct >= 80 ? 'rgba(0,168,165,0.82)' : pct >= 60 ? 'rgba(245,158,11,0.82)' : 'rgba(239,68,68,0.82)';
        }),
        borderColor: fireSafetyParams.map(p => {
          const pct = p.percentage ?? 0;
          return pct >= 80 ? 'rgba(0,140,138,1)' : pct >= 60 ? 'rgba(217,119,6,1)' : 'rgba(220,38,38,1)';
        }),
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 0.8, barPercentage: 1.0,
      },
      {
        label: 'Max Score',
        data: fireSafetyParams.map(p => p.maxScore),
        backgroundColor: 'rgba(203,213,225,0.40)',
        borderColor: 'rgba(148,163,184,0.7)',
        borderWidth: 1, borderRadius: 3,
        categoryPercentage: 0.8, barPercentage: 1.0,
      },
    ],
  }), [fireSafetyParams]);

  const chartOptions = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8, right: 120, bottom: 8, left: 4 } },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: { size: 12 }, padding: 18, usePointStyle: true, pointStyle: 'rect' as const,
          generateLabels: () => [
            { text: 'Score ≥ 80%', fillStyle: 'rgba(0,168,165,0.82)', strokeStyle: 'rgba(0,140,138,1)', lineWidth: 1.5, hidden: false },
            { text: 'Score 60–79%', fillStyle: 'rgba(245,158,11,0.82)', strokeStyle: 'rgba(217,119,6,1)', lineWidth: 1.5, hidden: false },
            { text: 'Score < 60%', fillStyle: 'rgba(239,68,68,0.82)', strokeStyle: 'rgba(220,38,38,1)', lineWidth: 1.5, hidden: false },
            { text: 'Max Score', fillStyle: 'rgba(203,213,225,0.50)', strokeStyle: 'rgba(148,163,184,0.7)', lineWidth: 1, hidden: false },
          ],
        },
      },
      title: {
        display: true,
        text: subtitleParts.length
          ? ['Fire Safety Parameters — Scores & % of Completion', subtitleParts.join(' · ')]
          : 'Fire Safety Parameters — Scores & % of Completion',
        font: { size: 14, weight: 'bold' as const },
        padding: { top: 4, bottom: 14 },
        color: '#1f2937',
      },
      tooltip: {
        backgroundColor: 'rgba(17,24,39,0.92)', padding: 12, cornerRadius: 8,
        callbacks: {
          title: (ctx: any) => fireSafetyParams[ctx[0]?.dataIndex]?.parameter ?? '',
          label: (ctx: any) => {
            const p = fireSafetyParams[ctx.dataIndex];
            if (!p) return '';
            if (ctx.datasetIndex === 0)
              return `Score: ${(p.score ?? 0).toFixed(2)} / ${p.maxScore} (${(p.percentage ?? 0).toFixed(1)}%)`;
            return `Max Score: ${p.maxScore}`;
          },
        },
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#374151', crossAlign: 'far' as const },
      },
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: { font: { size: 10 }, color: '#6b7280' },
        title: { display: true, text: 'Score', font: { size: 11, weight: 'bold' as const }, color: '#374151' },
      },
    },
  }), [fireSafetyParams, subtitleParts]);

  if (fireSafetyParams.length === 0) {
    return (
      <div className="w-full h-[400px] mt-4 bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-center">
        <p className="text-gray-400 text-base">No parameters data available</p>
      </div>
    );
  }

  return (
    <div className="w-full mt-4 bg-white p-4 rounded-lg border border-gray-200">
      {/* Header row: title + download */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Fire Safety Parameters</h3>
          {subtitleParts.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitleParts.join(' · ')}</p>
          )}
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors"
          title={`Download as ${buildFileName()}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PNG
        </button>
      </div>

      {/* Summary strip */}
      <div className="mb-5 flex items-center justify-center gap-10 bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-4 border border-blue-100">
        <div className="text-center">
          <div className="text-xs font-semibold text-gray-500 mb-1">Total Score</div>
          <div className="text-2xl font-bold text-blue-700">{totalScore.toFixed(2)}</div>
        </div>
        <div className="h-10 w-px bg-blue-200" />
        <div className="text-center">
          <div className="text-xs font-semibold text-gray-500 mb-1">Max Score</div>
          <div className="text-2xl font-bold text-blue-700">{totalMaxScore.toFixed(2)}</div>
        </div>
        <div className="h-10 w-px bg-blue-200" />
        <div className="text-center">
          <div className="text-xs font-semibold text-gray-500 mb-1">Aggregate %</div>
          <div className={`text-2xl font-bold ${aggPct >= 80 ? 'text-green-600' : aggPct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
            {aggPct.toFixed(1)}%
          </div>
        </div>
      </div>

      <div style={{ height: chartHeight }}>
        <Bar ref={chartRef} data={chartData} options={chartOptions as any} plugins={[barLabelPlugin]} />
      </div>
    </div>
  );
};

export default FireSafetyParametersChart;
